import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";

// Single-thread core (no cross-origin-isolation / SharedArrayBuffer needed).
const CORE = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";

let _ffmpeg = null;
let _loading = null;

async function getFFmpeg(onLog) {
  if (_ffmpeg) return _ffmpeg;
  if (_loading) return _loading;
  _loading = (async () => {
    const ff = new FFmpeg();
    if (onLog) ff.on("log", ({ message }) => onLog(message));
    await ff.load({
      coreURL: await toBlobURL(`${CORE}/ffmpeg-core.js`, "text/javascript"),
      wasmURL: await toBlobURL(`${CORE}/ffmpeg-core.wasm`, "application/wasm"),
    });
    _ffmpeg = ff;
    return ff;
  })();
  return _loading;
}

/**
 * Compress a phone video to ~720p H.264 MP4 in the browser so it uploads fast
 * on mobile data and plays on every device. Fully defensive: if the environment
 * can't transcode, it rejects and the caller uploads the original.
 *
 * @param {File} file
 * @param {(pct:number)=>void} onProgress  0..100 during transcode
 * @returns {Promise<File>} a smaller MP4 File (or throws to signal fallback)
 */
export async function compressVideo(file, onProgress) {
  const ff = await getFFmpeg();
  const inName = "input";
  const outName = "output.mp4";

  const handler = ({ progress }) => {
    if (onProgress && progress >= 0) onProgress(Math.min(99, Math.round(progress * 100)));
  };
  ff.on("progress", handler);

  try {
    await ff.writeFile(inName, await fetchFile(file));
    // Downscale longest side to 720p, H.264 + AAC, CRF 28 (good size/quality).
    await ff.exec([
      "-i", inName,
      "-vf", "scale='min(1280,iw)':'min(720,ih)':force_original_aspect_ratio=decrease",
      "-c:v", "libx264", "-preset", "veryfast", "-crf", "28",
      "-c:a", "aac", "-b:a", "128k",
      "-movflags", "+faststart",
      outName,
    ]);
    const data = await ff.readFile(outName);
    const blob = new Blob([data.buffer], { type: "video/mp4" });
    // Clean up FS to free memory.
    try { await ff.deleteFile(inName); await ff.deleteFile(outName); } catch (_) {}
    if (!blob.size) throw new Error("empty output");
    const name = (file.name || "walkthrough").replace(/\.[^.]+$/, "") + "-720p.mp4";
    return new File([blob], name, { type: "video/mp4" });
  } finally {
    ff.off("progress", handler);
  }
}
